import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar, Search, Plus, Pencil, Trash2,
  Loader2, X, ArrowLeft, MapPin, Clock,
} from "lucide-react";
import { supabase } from "../../lib/supabaseClient";
import { useToast } from "../ui/Toast";
import ConfirmDialog from "../ui/ConfirmDialog";

const AdminEvents = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [events, setEvents] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [collegeFilter, setCollegeFilter] = useState("all");

  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const [formTitle, setFormTitle] = useState("");
  const [formDate, setFormDate] = useState("");
  const [formTime, setFormTime] = useState("");
  const [formLocation, setFormLocation] = useState("");
  const [formCollege, setFormCollege] = useState("All Colleges");
  const [formErrors, setFormErrors] = useState({});

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchColleges = useCallback(async () => {
    const { data } = await supabase.from("colleges").select("id, name").order("name");
    if (data) setColleges(data);
  }, []);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("events")
      .select("*, event_registrations(count)")
      .order("created_at", { ascending: false });

    if (error) {
      toast.error("Failed to load events: " + error.message);
    } else {
      setEvents(data || []);
    }
    setLoading(false);
  }, [toast]);

  useEffect(() => {
    fetchEvents();
    fetchColleges();
  }, [fetchEvents, fetchColleges]);

  const openAddModal = () => {
    setEditingEvent(null);
    setFormTitle("");
    setFormDate(new Date().toISOString().slice(0, 10));
    setFormTime("10:00 AM - 02:00 PM");
    setFormLocation("");
    setFormCollege("All Colleges");
    setFormErrors({});
    setShowModal(true);
  };

  const openEditModal = (evt) => {
    setEditingEvent(evt);
    setFormTitle(evt.title);
    setFormDate(evt.date);
    setFormTime(evt.time);
    setFormLocation(evt.location);
    setFormCollege(evt.college_name || "All Colleges");
    setFormErrors({});
    setShowModal(true);
  };

  const validate = () => {
    const errs = {};
    if (!formTitle.trim()) errs.title = "Title is required";
    if (!formDate.trim()) errs.date = "Date is required";
    if (!formTime.trim()) errs.time = "Time is required";
    if (!formLocation.trim()) errs.location = "Location is required";
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setModalLoading(true);

    const payload = {
      title: formTitle.trim(),
      date: formDate.trim(),
      time: formTime.trim(),
      location: formLocation.trim(),
      college_name: formCollege === "All Colleges" ? null : formCollege,
    };

    if (editingEvent) {
      const { error } = await supabase.from("events").update(payload).eq("id", editingEvent.id);
      if (error) {
        toast.error("Failed to update event: " + error.message);
      } else {
        toast.success("Event updated successfully");
        setShowModal(false);
        fetchEvents();
      }
    } else {
      const { error } = await supabase.from("events").insert(payload);
      if (error) {
        toast.error("Failed to create event: " + error.message);
      } else {
        toast.success("Event created successfully");
        setShowModal(false);
        fetchEvents();
      }
    }
    setModalLoading(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);

    const { error } = await supabase.from("events").delete().eq("id", deleteTarget.id);
    if (error) {
      toast.error("Failed to delete event: " + error.message);
    } else {
      toast.success("Event deleted");
      fetchEvents();
    }
    setDeleteTarget(null);
    setDeleteLoading(false);
  };

  const filteredEvents = events.filter((e) => {
    if (collegeFilter !== "all") {
      if (e.college_name && e.college_name !== collegeFilter) return false;
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return e.title.toLowerCase().includes(q) || e.location.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-ku-cream">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1a1a2e] via-[#2d1b4e] to-[#3B1E60] text-white py-8 md:py-10 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <button
              type="button"
              onClick={() => navigate("/admin/dashboard")}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-ku-gold hover:text-amber-300 transition-colors bg-transparent border-none cursor-pointer mb-3 p-0"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </button>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight">Manage Events</h1>
            <p className="text-white/60 text-sm mt-1 font-medium">Create and schedule college-specific volunteering drives</p>
          </div>
          <button
            type="button"
            onClick={openAddModal}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-ku-gold text-ku-purple-deep text-sm font-bold hover:bg-amber-400 transition-colors cursor-pointer shadow-sm border-none self-start sm:self-auto"
          >
            <Plus className="w-4 h-4" /> Add Event
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="bg-white rounded-2xl border border-ku-soft/50 shadow-sm p-4 mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search event title or location..."
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-ku-purple bg-ku-cream/30 focus:border-ku-purple outline-none"
            />
          </div>
          <select
            value={collegeFilter}
            onChange={(e) => setCollegeFilter(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-ku-purple bg-ku-cream/30 focus:border-ku-purple outline-none"
          >
            <option value="all">All Colleges Filter</option>
            {colleges.map((c) => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-ku-purple animate-spin" />
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="bg-white rounded-2xl border border-purple-100 shadow-sm flex flex-col items-center justify-center py-20 text-gray-400">
            <Calendar className="w-12 h-12 mb-3 text-purple-300" />
            <p className="font-bold text-gray-500">No events found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEvents.map((evt) => (
              <div key={evt.id} className="bg-white rounded-2xl border border-purple-100 shadow-sm p-5 hover:shadow-md transition-all flex flex-col justify-between">
                <div>
                  <div className="flex items-start justify-between">
                    <span className="text-[10px] font-black uppercase px-2.5 py-1 rounded-full bg-purple-100 text-purple-800">
                      {evt.college_name || "All Colleges"}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => openEditModal(evt)}
                        className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 border-none cursor-pointer"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleteTarget(evt)}
                        className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 border-none cursor-pointer"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <h3 className="font-bold text-gray-900 text-base mt-3">{evt.title}</h3>
                  <div className="mt-3 space-y-1.5 text-xs text-gray-500 font-medium">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-purple-600" />
                      <span>{evt.date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-purple-600" />
                      <span>{evt.time}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-purple-600" />
                      <span className="truncate">{evt.location}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between text-xs font-bold text-gray-600">
                  <span>Registered Volunteers</span>
                  <span className="bg-emerald-50 text-emerald-700 px-2.5 py-0.5 rounded-full border border-emerald-200">
                    {evt.event_registrations?.[0]?.count || 0}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowModal(false)} className="fixed inset-0 z-[9990] bg-black/40 backdrop-blur-sm" />
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="fixed inset-0 z-[9991] flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden">
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                  <h3 className="text-lg font-black text-ku-purple">{editingEvent ? "Edit Event" : "Create New Event"}</h3>
                  <button type="button" onClick={() => setShowModal(false)} className="p-1 rounded-lg text-gray-400 hover:text-gray-600 border-none bg-transparent cursor-pointer"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 space-y-4">
                  <div>
                    <label className="block text-xs font-black text-ku-purple uppercase tracking-wider mb-1">Event Title *</label>
                    <input type="text" value={formTitle} onChange={(e) => setFormTitle(e.target.value)} placeholder="e.g. Joy of Giving Camp" className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-ku-purple outline-none focus:border-ku-purple" />
                    {formErrors.title && <p className="text-xs text-red-500 font-bold mt-1">{formErrors.title}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-black text-ku-purple uppercase tracking-wider mb-1">Assign to College</label>
                    <select value={formCollege} onChange={(e) => setFormCollege(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-ku-purple outline-none focus:border-ku-purple">
                      <option value="All Colleges">All Colleges (Open for Everyone)</option>
                      {colleges.map((c) => (
                        <option key={c.id} value={c.name}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-black text-ku-purple uppercase tracking-wider mb-1">Date *</label>
                      <input type="text" value={formDate} onChange={(e) => setFormDate(e.target.value)} placeholder="e.g. Sep 15, 2026" className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-ku-purple outline-none focus:border-ku-purple" />
                      {formErrors.date && <p className="text-xs text-red-500 font-bold mt-1">{formErrors.date}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-black text-ku-purple uppercase tracking-wider mb-1">Time *</label>
                      <input type="text" value={formTime} onChange={(e) => setFormTime(e.target.value)} placeholder="e.g. 10:00 AM - 02:00 PM" className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-ku-purple outline-none focus:border-ku-purple" />
                      {formErrors.time && <p className="text-xs text-red-500 font-bold mt-1">{formErrors.time}</p>}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-black text-ku-purple uppercase tracking-wider mb-1">Location *</label>
                    <input type="text" value={formLocation} onChange={(e) => setFormLocation(e.target.value)} placeholder="e.g. Bandra Campus Ground" className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm font-bold text-ku-purple outline-none focus:border-ku-purple" />
                    {formErrors.location && <p className="text-xs text-red-500 font-bold mt-1">{formErrors.location}</p>}
                  </div>
                </div>
                <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100">
                  <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 rounded-xl text-sm font-bold text-gray-600 bg-gray-100 border-none cursor-pointer">Cancel</button>
                  <button type="button" onClick={handleSave} disabled={modalLoading} className="px-5 py-2 rounded-xl text-sm font-bold text-white bg-ku-purple hover:bg-ku-violet border-none cursor-pointer flex items-center gap-2">
                    {modalLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Event"}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Event"
        message={`Are you sure you want to delete "${deleteTarget?.title}"?`}
        confirmText="Delete Event"
        loading={deleteLoading}
      />
    </div>
  );
};

export default AdminEvents;